import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { LOGO_URL } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Edit3, Check, X as XIcon, Star, Mail, Lock, Eye, EyeOff } from "lucide-react";
import { GalleriesAdmin } from "@/pages/admin/GalleryEditor";
import MediaLibraryAdmin from "@/pages/admin/MediaLibrary";
import ImageField from "@/components/admin/ImageField";
import HomepageEditor from "@/pages/admin/HomepageEditor";
import EmailTemplatesAdmin, { LeadEmailHistoryModal } from "@/pages/admin/EmailTemplatesAdmin";
import { WhyChooseAdmin, ProcessStepsAdmin } from "@/pages/admin/CmsListEditors";
import ServicesAdmin from "@/pages/admin/ServicesAdmin";
import ReviewsAdmin from "@/pages/admin/ReviewsAdmin";
import ContractsAdmin from "@/pages/admin/ContractsAdmin";
import NationalitiesAdmin from "@/pages/admin/NationalitiesAdmin";

/* ---------------- Change Password Form ---------------- */
function GalleryMultiSelect({ value = [], onChange, testId }) {
  const [galleries, setGalleries] = useState([]);
  useEffect(() => {
    api.get("/admin/galleries").then(r => setGalleries(r.data)).catch(() => {});
  }, []);
  const toggle = (slug) => {
    if (value.includes(slug)) onChange(value.filter(s => s !== slug));
    else onChange([...value, slug]);
  };
  return (
    <div className="mt-2 max-h-56 overflow-y-auto border border-white/15 bg-black p-2 grid grid-cols-1 sm:grid-cols-2 gap-1" data-testid={testId}>
      {galleries.length === 0 ? (
        <div className="text-xs text-white/40 p-2">No galleries yet. Create one in the Galleries tab.</div>
      ) : galleries.map(g => (
        <label key={g.id} className={`flex items-center gap-2 p-2 text-sm cursor-pointer hover:bg-white/5 ${value.includes(g.slug) ? "bg-[#D4AF37]/10" : ""}`}
          data-testid={`${testId}-${g.slug}`}>
          <input type="checkbox" checked={value.includes(g.slug)} onChange={() => toggle(g.slug)} className="accent-[#D4AF37]" />
          <span className="truncate">{g.title}</span>
          <span className="ml-auto text-xs text-white/40 truncate">{g.slug}</span>
        </label>
      ))}
    </div>
  );
}

/* ---------------- Change Password Form ---------------- */
function ChangePasswordForm() {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [show, setShow] = useState({ current: false, next: false });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.next !== form.confirm) { toast.error("New passwords do not match"); return; }
    if (form.next.length < 10) { toast.error("New password must be at least 10 characters"); return; }
    if (/\s/.test(form.next)) { toast.error("Password cannot contain spaces"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      toast.success("Password updated. Use the new password on your next sign-in.");
      setForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not change password");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="bg-[#0a0a0a] border border-white/10 p-5 space-y-3" data-testid="change-password-form">
      <div className="flex items-center gap-3 mb-1">
        <Lock size={16} className="text-[#D4AF37]" />
        <h3 className="font-serif text-xl">Security — Change Password</h3>
      </div>
      <p className="text-xs text-white/50 -mt-1">Choose a new password of 10+ characters, no spaces. Takes effect immediately on next sign-in.</p>
      {[
        { k: "current", label: "Current password", testid: "cp-current", showState: "current" },
        { k: "next", label: "New password", testid: "cp-next", showState: "next" },
        { k: "confirm", label: "Confirm new password", testid: "cp-confirm" },
      ].map(({ k, label, testid, showState }) => (
        <label key={k} className="block">
          <span className="text-[0.6rem] tracking-[0.25em] uppercase text-white/60">{label}</span>
          <div className="relative mt-1">
            <input
              type={showState && show[showState] ? "text" : "password"}
              value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })}
              autoComplete={k === "current" ? "current-password" : "new-password"}
              data-testid={testid}
              className="w-full bg-black border border-white/15 px-3 py-2 pr-10 text-sm focus:border-[#D4AF37] outline-none"
            />
            {showState && (
              <button type="button" onClick={() => setShow({ ...show, [showState]: !show[showState] })}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-white/40 hover:text-[#D4AF37] p-1">
                {show[showState] ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            )}
          </div>
        </label>
      ))}
      <button type="submit" disabled={busy} className="btn-gold" data-testid="change-password-submit">
        <Lock size={14} /> {busy ? "Updating..." : "Update Password"}
      </button>
    </form>
  );
}

const TABS = [
  { key: "leads", label: "Leads" },
  { key: "consultations", label: "Consultations" },
  { key: "homepage", label: "Homepage" },
  { key: "services", label: "Services" },
  { key: "why-choose", label: "Why Choose Us" },
  { key: "process-steps", label: "Our Process" },
  { key: "email-templates", label: "Email Templates" },
  { key: "testimonials", label: "Testimonials" },
  { key: "reviews", label: "Google Reviews" },
  { key: "nationalities", label: "Wedding Couple Nationalities" },
  { key: "contracts", label: "Contracts" },
  { key: "galleries", label: "Galleries" },
  { key: "media", label: "Media Library" },
  { key: "portfolio", label: "Portfolio" },
  { key: "films", label: "Wedding Films" },
  { key: "blog", label: "Blog" },
  { key: "packages", label: "Packages" },
  { key: "faqs", label: "FAQs" },
  { key: "settings", label: "Settings" },
];

const SCHEMA = {
  testimonials: ["name","role","venue","rating","review","photo_url","approved","featured"],
  portfolio: ["title","category","image_url","featured","order","gallery_slugs"],
  films: ["title","youtube_id","category","description","featured","order"],
  blog: ["title","slug","category","excerpt","content","cover_image","author","published","seo_title","seo_description"],
  packages: ["name","price","description","features","highlighted","order"],
  faqs: ["question","answer","category","order"],
};

const BOOLEAN_FIELDS = ["featured","approved","highlighted","published"];
const ARRAY_FIELDS = ["features","gallery"];
const TEXTAREA_FIELDS = ["content","story","description","review","answer","excerpt","seo_description"];
const IMAGE_FIELDS = ["image_url","cover_image","photo_url"];
const GALLERY_MULTI_SELECT_FIELDS = ["gallery_slugs"];

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("leads");
  const [rows, setRows] = useState([]);
  const [tabCache, setTabCache] = useState({}); // {tabKey: rows[]} — instant nav back
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [settings, setSettings] = useState({});
  const [emailHistory, setEmailHistory] = useState(null); // { id, name }

  useEffect(() => {
    if (!localStorage.getItem("ep_token")) nav("/admin/login");
  }, [nav]);

  const load = async (force = false) => {
    if (tab === "settings") {
      const r = await api.get("/settings"); setSettings(r.data); return;
    }
    if (tab === "galleries" || tab === "media" || tab === "homepage" || tab === "email-templates" || tab === "why-choose" || tab === "process-steps" || tab === "services" || tab === "reviews" || tab === "contracts" || tab === "nationalities") return;
    // Use cache for instant nav, then revalidate in background (stale-while-revalidate)
    if (!force && tabCache[tab]) {
      setRows(tabCache[tab]);
    } else {
      setLoading(true);
    }
    try {
      const r = await api.get(`/admin/${tab}`);
      setRows(r.data);
      setTabCache(prev => ({ ...prev, [tab]: r.data }));
    } catch (e) {
      if (e.response?.status === 401) { localStorage.removeItem("ep_token"); nav("/admin/login"); }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const logout = async () => {
    localStorage.removeItem("ep_token");
    nav("/admin/login");
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/admin/${tab}/${id}`);
    toast.success("Deleted");
    setTabCache(prev => ({ ...prev, [tab]: undefined }));
    load(true);
  };

  const save = async (item) => {
    const payload = { ...item };
    if (payload.id == null) delete payload.id;
    ARRAY_FIELDS.forEach(k => { if (typeof payload[k] === "string") payload[k] = payload[k].split("\n").map(s=>s.trim()).filter(Boolean); });
    try {
      if (item.id && rows.find(r => r.id === item.id)) {
        await api.put(`/admin/${tab}/${item.id}`, payload);
      } else {
        await api.post(`/admin/${tab}`, payload);
      }
      toast.success("Saved");
      setEditing(null); setCreating(false);
      setTabCache(prev => ({ ...prev, [tab]: undefined }));
      load(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const saveSettings = async () => {
    await api.put("/admin/settings", settings);
    toast.success("Settings saved");
  };

  const fields = SCHEMA[tab] || [];

  return (
    <div className="min-h-screen bg-[#070707] flex" data-testid="admin-dashboard">
      <aside className="w-64 border-r border-white/10 p-6 sticky top-0 h-screen flex flex-col">
        <div className="flex items-center gap-3 mb-10">
          <img src={LOGO_URL} alt="" className="h-12 object-contain" />
          <div>
            <div className="font-serif text-base leading-tight">Elite Pixels</div>
            <div className="text-[0.55rem] tracking-[0.25em] uppercase text-white/40">Admin</div>
          </div>
        </div>
        <nav className="space-y-1 flex-1">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`block w-full text-left px-4 py-2.5 text-sm tracking-wide transition ${tab===t.key?"bg-[#D4AF37] text-black":"text-white/70 hover:bg-white/5"}`}>
              {t.label}
            </button>
          ))}
        </nav>
        <button onClick={logout} className="text-xs text-white/40 hover:text-[#D4AF37] flex items-center gap-2 mt-4" data-testid="logout-btn">
          <LogOut size={14}/> Logout
        </button>
      </aside>

      <main className="flex-1 p-8 overflow-x-auto">
        <div className="flex items-center justify-between mb-8">
          <h2 className="font-serif text-3xl capitalize">{tab.replace("-"," ")}</h2>
          {tab === "blog" && (
            <button onClick={() => nav("/admin/blog/new")} className="btn-gold" data-testid="new-blog-btn">
              <Plus size={14}/> New Post
            </button>
          )}
          {tab !== "leads" && tab !== "settings" && tab !== "consultations" && tab !== "galleries" && tab !== "media" && tab !== "blog" && tab !== "homepage" && tab !== "email-templates" && tab !== "why-choose" && tab !== "process-steps" && tab !== "services" && tab !== "reviews" && tab !== "contracts" && tab !== "nationalities" && (
            <button onClick={() => { setCreating(true); setEditing({ id: null, ...Object.fromEntries(fields.map(f => [f, BOOLEAN_FIELDS.includes(f) ? false : ARRAY_FIELDS.includes(f) ? [] : (f==="rating"||f==="order"?0:"")])) }); }}
              className="btn-gold" data-testid="new-item-btn">
              <Plus size={14}/> New
            </button>
          )}
        </div>

        {tab === "galleries" && <GalleriesAdmin />}
        {tab === "media" && <MediaLibraryAdmin />}
        {tab === "homepage" && <HomepageEditor />}
        {tab === "email-templates" && <EmailTemplatesAdmin />}
        {tab === "why-choose" && <WhyChooseAdmin />}
        {tab === "process-steps" && <ProcessStepsAdmin />}
        {tab === "services" && <ServicesAdmin />}
        {tab === "reviews" && <ReviewsAdmin />}
        {tab === "contracts" && <ContractsAdmin />}
        {tab === "nationalities" && <NationalitiesAdmin />}

        {tab === "settings" && (
          <div className="max-w-2xl space-y-4" data-testid="settings-form">
            <ChangePasswordForm />
            <div className="bg-[#0a0a0a] border border-[#D4AF37]/30 p-4 flex items-center justify-between gap-4">
              <div>
                <div className="text-sm text-white font-medium">Cinematic Logo Intro</div>
                <div className="text-xs text-white/50 mt-0.5">Preview the animation on the live homepage with your current settings.</div>
              </div>
              <a href="/?preview_intro=1" target="_blank" rel="noreferrer" className="btn-gold text-xs whitespace-nowrap" data-testid="preview-intro-btn">
                Preview ›
              </a>
            </div>
            {Object.entries(settings).filter(([k]) => k !== "_id" && k !== "id").map(([k, v]) => {
              const isBool = typeof v === "boolean";
              const isNum = typeof v === "number";
              const isLongText = ["popup_message"].includes(k) || (typeof v === "string" && v.length > 120);
              return (
                <label key={k} className="block">
                  <span className="text-[0.65rem] tracking-[0.25em] uppercase text-white/60">{k.replace(/_/g, " ")}</span>
                  {isBool ? (
                    <label className="flex items-center gap-2 mt-2">
                      <input type="checkbox" checked={!!v}
                        onChange={e => setSettings({...settings, [k]: e.target.checked})}
                        className="accent-[#D4AF37]" data-testid={`settings-${k}`}/>
                      <span className="text-sm text-white/80">{v ? "Enabled" : "Disabled"}</span>
                    </label>
                  ) : isLongText ? (
                    <textarea rows={3} value={v ?? ""}
                      onChange={e => setSettings({...settings, [k]: e.target.value})}
                      data-testid={`settings-${k}`}
                      className="mt-1 w-full bg-black border border-white/15 px-4 py-2.5 text-sm focus:border-[#D4AF37] outline-none"/>
                  ) : (
                    <input type={isNum ? "number" : "text"} value={v ?? ""}
                      onChange={e => setSettings({...settings, [k]: isNum ? parseFloat(e.target.value) : e.target.value})}
                      data-testid={`settings-${k}`}
                      className="mt-1 w-full bg-black border border-white/15 px-4 py-2.5 text-sm focus:border-[#D4AF37] outline-none"/>
                  )}
                </label>
              );
            })}
            <button onClick={saveSettings} className="btn-gold" data-testid="settings-save">Save Settings</button>
          </div>
        )}

        {tab === "leads" && (
          <div className="overflow-x-auto">
            <div className="mb-4 flex items-center gap-3 text-xs">
              <button onClick={async () => { const r = await api.post('/admin/drips/run-now'); toast.success(`Drip pass complete — ${r.data.sent} sent`); load(); }}
                className="btn-ghost" data-testid="run-drips-btn">Run Drip Pass Now</button>
              <span className="text-white/40">Auto-runs every hour. Day 2 / 5 / 7 follow-ups.</span>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-[#0a0a0a] text-white/60 text-xs tracking-[0.18em] uppercase">
                <tr>{["Score","Name","Email","Phone","Wedding","Venue","City","P/V","Drips","Status",""].map(h=> <th key={h} className="text-left p-3">{h}</th>)}</tr>
              </thead>
              <tbody>
                {rows.map(l => {
                  const tier = l.score_tier || "cold";
                  const tierColor = tier === "hot" ? "bg-red-500/20 text-red-300 border-red-500/40" : tier === "warm" ? "bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/40" : "bg-white/5 text-white/40 border-white/10";
                  const drips = l.drips_sent || [];
                  return (
                    <tr key={l.id} className="border-b border-white/5" data-testid={`lead-row-${l.id}`}>
                      <td className="p-3">
                        <div className={`inline-flex items-center gap-2 px-2 py-1 border ${tierColor}`}>
                          <span className="font-mono text-xs">{l.score ?? 0}</span>
                          <span className="text-[0.6rem] tracking-[0.2em] uppercase">{tier}</span>
                        </div>
                      </td>
                      <td className="p-3">{l.name}</td>
                      <td className="p-3"><a href={`mailto:${l.email}`} className="hover:text-[#D4AF37]">{l.email}</a></td>
                      <td className="p-3">{l.phone}</td>
                      <td className="p-3">{l.wedding_date || "—"}</td>
                      <td className="p-3 max-w-[140px] truncate">{l.venue || "—"}</td>
                      <td className="p-3">{l.city || "—"}</td>
                      <td className="p-3 text-[#D4AF37]">{l.photography?"P":""} {l.videography?"V":""}</td>
                      <td className="p-3">
                        <div className="flex gap-1">
                          {["day_0","day_2","day_5","day_7"].map(k => {
                            const sent = drips.includes(k);
                            return (
                              <button key={k} title={`${k.replace('_',' ')} — ${sent ? 'sent' : 'pending'}`}
                                onClick={async () => {
                                  if (k === "day_0") return;
                                  if (!window.confirm(`Send ${k.replace('_',' ')} drip to ${l.email}?`)) return;
                                  try { await api.post(`/admin/leads/${l.id}/send-drip/${k}`); toast.success(`${k} sent`); load(); }
                                  catch (e) { toast.error("Send failed"); }
                                }}
                                data-testid={`drip-${l.id}-${k}`}
                                className={`w-7 h-7 text-[10px] font-mono transition ${sent ? "bg-[#D4AF37]/30 text-[#D4AF37] border border-[#D4AF37]/40" : "border border-white/15 text-white/40 hover:border-[#D4AF37] hover:text-[#D4AF37]"}`}>
                                {k.split("_")[1]}
                              </button>
                            );
                          })}
                        </div>
                      </td>
                      <td className="p-3">
                        <select value={l.status} onChange={async e=>{
                          const newStatus = e.target.value;
                          await api.put(`/admin/leads/${l.id}`,{status: newStatus});
                          if (l.status !== newStatus) toast.success(`Status → ${newStatus}. Email queued to ${l.email}`);
                          load();
                        }}
                          data-testid={`lead-status-${l.id}`}
                          className="bg-black border border-white/15 px-2 py-1 text-xs">
                          <option value="new">new</option><option value="contacted">contacted</option>
                          <option value="booked">booked</option><option value="closed">closed</option>
                        </select>
                        {l.last_status_email_at && (
                          <div className="text-[0.55rem] text-white/30 mt-1">
                            Last email: {l.last_status_email_status === "sent" ? "✓" : "✗"} {new Date(l.last_status_email_at).toLocaleDateString()}
                          </div>
                        )}
                      </td>
                      <td className="p-3 whitespace-nowrap">
                        <button onClick={() => setEmailHistory({ id: l.id, name: l.name })}
                          title="Email history" className="text-white/40 hover:text-[#D4AF37] mr-2"
                          data-testid={`lead-email-history-${l.id}`}>
                          <Mail size={14}/>
                        </button>
                        <button onClick={() => remove(l.id)} className="text-white/40 hover:text-red-500"><Trash2 size={14}/></button>
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && <tr><td colSpan={11} className="p-12 text-center text-white/40">No leads yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "consultations" && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#0a0a0a] text-white/60 text-xs tracking-[0.18em] uppercase">
                <tr>{["Date","Time","Name","Email","Phone","Notes","Status",""].map(h=> <th key={h} className="text-left p-3">{h}</th>)}</tr>
              </thead>
              <tbody>
                {rows.map(c => (
                  <tr key={c.id} className="border-b border-white/5" data-testid={`consult-row-${c.id}`}>
                    <td className="p-3 text-[#D4AF37]">{c.date}</td>
                    <td className="p-3 text-[#D4AF37]">{c.time}</td>
                    <td className="p-3">{c.name}</td>
                    <td className="p-3"><a href={`mailto:${c.email}`} className="hover:text-[#D4AF37]">{c.email}</a></td>
                    <td className="p-3">{c.phone}</td>
                    <td className="p-3 max-w-xs truncate">{c.message}</td>
                    <td className="p-3">
                      <select value={c.status} onChange={async e=>{ await api.put(`/admin/consultations/${c.id}`,{status:e.target.value}); load(); }}
                        className="bg-black border border-white/15 px-2 py-1 text-xs">
                        <option value="scheduled">scheduled</option>
                        <option value="completed">completed</option>
                        <option value="cancelled">cancelled</option>
                      </select>
                    </td>
                    <td className="p-3"><button onClick={() => remove(c.id)} className="text-white/40 hover:text-red-500"><Trash2 size={14}/></button></td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={8} className="p-12 text-center text-white/40">No consultations booked yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab !== "leads" && tab !== "settings" && tab !== "consultations" && tab !== "galleries" && tab !== "media" && tab !== "homepage" && tab !== "email-templates" && tab !== "why-choose" && tab !== "process-steps" && tab !== "services" && tab !== "reviews" && tab !== "contracts" && tab !== "nationalities" && (
          <div className="grid gap-3">
            {rows.map(r => (
              <div key={r.id} className="bg-[#0a0a0a] border border-white/10 p-5 flex items-start justify-between gap-4" data-testid={`row-${r.id}`}>
                <div className="flex items-start gap-4 flex-1 min-w-0">
                  {r.image_url && <img src={r.image_url} alt="" className="w-20 h-20 object-cover" />}
                  {r.cover_image && <img src={r.cover_image} alt="" className="w-20 h-20 object-cover" />}
                  {r.photo_url && <img src={r.photo_url} alt="" className="w-16 h-16 object-cover rounded-full" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-serif text-lg truncate">{r.title || r.question || r.name || r.couple_names}</div>
                    <div className="text-xs text-white/50 mt-1 truncate">{r.category || r.venue || r.role || r.price || r.excerpt}</div>
                    {tab === "testimonials" && (
                      <div className="flex gap-2 mt-2 text-xs">
                        <span className={r.approved?"text-green-400":"text-yellow-400"}>{r.approved?"Approved":"Pending"}</span>
                        {r.featured && <span className="text-[#D4AF37]">Featured</span>}
                        <span className="flex gap-0.5">{[...Array(r.rating||5)].map((_,i)=><Star key={i} size={10} className="fill-[#D4AF37] text-[#D4AF37]"/>)}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => tab === "blog" ? nav(`/admin/blog/${r.id}`) : setEditing({...r})}
                    className="text-white/60 hover:text-[#D4AF37]" data-testid={`edit-${r.id}`}><Edit3 size={16}/></button>
                  <button onClick={() => remove(r.id)} className="text-white/60 hover:text-red-500" data-testid={`del-${r.id}`}><Trash2 size={16}/></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Edit Modal */}
        {editing && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur z-50 flex items-center justify-center p-6 overflow-y-auto" data-testid="edit-modal">
            <div className="bg-[#0a0a0a] border border-white/15 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-white/10 flex items-center justify-between sticky top-0 bg-[#0a0a0a]">
                <h3 className="font-serif text-2xl">{creating ? "New" : "Edit"} {tab}</h3>
                <button onClick={() => {setEditing(null); setCreating(false);}}><XIcon size={20}/></button>
              </div>
              <div className="p-6 space-y-4">
                {fields.map(k => (
                  <label key={k} className="block">
                    <span className="text-[0.65rem] tracking-[0.25em] uppercase text-white/60">{k.replace(/_/g," ")}</span>
                    {IMAGE_FIELDS.includes(k) ? (
                      <div className="mt-2">
                        <ImageField value={editing[k] || ""} onChange={(url) => setEditing({...editing, [k]: url})}
                          variant={k === "photo_url" ? "medium" : "large"}
                          aspect={k === "photo_url" ? "aspect-square max-w-[200px]" : "aspect-video"}
                          testId={`field-${k}`} />
                      </div>
                    ) : BOOLEAN_FIELDS.includes(k) ? (
                      <label className="flex items-center gap-2 mt-2">
                        <input type="checkbox" checked={!!editing[k]} onChange={e => setEditing({...editing,[k]: e.target.checked})} className="accent-[#D4AF37]" />
                        <span className="text-sm">{k}</span>
                      </label>
                    ) : ARRAY_FIELDS.includes(k) ? (
                      <textarea rows={4} value={Array.isArray(editing[k]) ? editing[k].join("\n") : (editing[k] || "")}
                        onChange={e => setEditing({...editing,[k]: e.target.value})}
                        placeholder="One item per line"
                        className="mt-1 w-full bg-black border border-white/15 px-3 py-2 text-sm focus:border-[#D4AF37] outline-none"/>
                    ) : GALLERY_MULTI_SELECT_FIELDS.includes(k) ? (
                      <GalleryMultiSelect value={editing[k] || []} onChange={(v) => setEditing({...editing, [k]: v})} testId={`field-${k}`} />
                    ) : TEXTAREA_FIELDS.includes(k) ? (
                      <textarea rows={6} value={editing[k] || ""} onChange={e => setEditing({...editing,[k]: e.target.value})}
                        className="mt-1 w-full bg-black border border-white/15 px-3 py-2 text-sm focus:border-[#D4AF37] outline-none"/>
                    ) : (
                      <input type={k === "rating" || k === "order" ? "number" : "text"}
                        value={editing[k] ?? ""} onChange={e => setEditing({...editing,[k]: (k==="rating"||k==="order")?parseInt(e.target.value||"0"):e.target.value})}
                        className="mt-1 w-full bg-black border border-white/15 px-3 py-2 text-sm focus:border-[#D4AF37] outline-none"/>
                    )}
                  </label>
                ))}
                <div className="flex gap-3 pt-2">
                  <button onClick={() => save(editing)} className="btn-gold" data-testid="modal-save"><Check size={14}/> Save</button>
                  <button onClick={() => {setEditing(null); setCreating(false);}} className="btn-ghost">Cancel</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {emailHistory && (
          <LeadEmailHistoryModal leadId={emailHistory.id} leadName={emailHistory.name} onClose={() => setEmailHistory(null)} />
        )}
      </main>
    </div>
  );
}
